import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Bell, MapPin } from "lucide-react";
import Layout from "@/components/Layout";
import BookingDetailDrawer from "@/components/BookingDetailDrawer";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import api, { formatApiError } from "@/lib/api";

const ACTIVE = new Set([
  "confirmed",
  "dispatched",
  "en_route",
  "chauffeur_arrived",
  "passenger_loaded",
]);
const PAST = new Set(["completed", "cancelled"]);

const STATUS_LABELS = {
  confirmed: "Confirmed",
  dispatched: "Dispatched",
  en_route: "Chauffeur En Route",
  chauffeur_arrived: "Chauffeur Arrived",
  passenger_loaded: "In Transit",
  completed: "Completed",
  cancelled: "Cancelled",
};

const TABS = [
  { key: "active", label: "Active" },
  { key: "past", label: "Past" },
  { key: "profile", label: "Profile" },
];

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Account() {
  const { user, profile, loading, logout } = useAuth();
  const { newSinceLastPoll, unreadCount } = useNotifications();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("active");
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsError, setBookingsError] = useState("");
  const [openBooking, setOpenBooking] = useState(null);

  const fetchBookings = useCallback(async () => {
    setBookingsLoading(true);
    setBookingsError("");
    try {
      const { data } = await api.get("/bookings/mine");
      setBookings(data || []);
      return data || [];
    } catch (e) {
      setBookingsError(formatApiError(e));
      return [];
    } finally {
      setBookingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchBookings();
  }, [user, fetchBookings]);

  // When a new notification arrives, refresh booking statuses too
  useEffect(() => {
    if (newSinceLastPoll && newSinceLastPoll.length > 0) {
      fetchBookings();
    }
  }, [newSinceLastPoll, fetchBookings]);

  // Auto-open booking drawer if landed with ?b=<id> (from notifications dropdown)
  const targetBookingId = searchParams.get("b");
  useEffect(() => {
    if (!targetBookingId || bookings.length === 0) return;
    const match = bookings.find((b) => b.id === targetBookingId);
    if (match) {
      setOpenBooking(match);
      // Clear the query param so back-button feels right
      const params = new URLSearchParams(searchParams);
      params.delete("b");
      setSearchParams(params, { replace: true });
    }
  }, [targetBookingId, bookings, searchParams, setSearchParams]);

  const { active, past } = useMemo(() => {
    const a = [];
    const p = [];
    for (const b of bookings) {
      if (ACTIVE.has(b.status)) a.push(b);
      else if (PAST.has(b.status)) p.push(b);
      else a.push(b);
    }
    return { active: a, past: p };
  }, [bookings]);

  if (loading) {
    return (
      <Layout>
        <div
          data-testid="account-loading"
          className="min-h-screen flex items-center justify-center text-[#8E9AA6] text-xs uppercase tracking-[0.3em]"
        >
          Loading…
        </div>
      </Layout>
    );
  }

  if (!user) return <Navigate to="/login" state={{ from: "/account" }} replace />;

  const initials =
    `${profile?.first_name?.[0] || ""}${profile?.last_name?.[0] || ""}`.toUpperCase() ||
    user.email[0].toUpperCase();

  return (
    <Layout>
      <section className="pt-32 pb-24 bg-[#0B0B0C]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
            <div>
              <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-5">
                Concierge Dashboard
              </div>
              <h1
                data-testid="account-greeting"
                className="font-serif text-4xl md:text-5xl font-semibold tracking-tighter text-white"
              >
                Good to see you, {profile?.first_name || user.email.split("@")[0]}.
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/book"
                data-testid="dashboard-book-new-btn"
                className="inline-flex items-center gap-2 bg-white text-black hover:bg-[#E6E6E6] px-7 py-3.5 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
              >
                <Plus size={14} strokeWidth={1.5} /> New Reservation
              </Link>
              <div
                data-testid="account-avatar"
                className="h-14 w-14 border border-white/20 backdrop-blur-xl bg-[#121214]/60 flex items-center justify-center font-serif text-xl text-white"
              >
                {initials}
              </div>
            </div>
          </div>

          {/* Notification banner area (real-time status updates) */}
          <div
            data-testid="dashboard-notifications"
            className="mb-10 border border-white/10 bg-[#121214]/40 backdrop-blur-md px-6 py-4 flex items-center gap-4"
          >
            <Bell size={16} strokeWidth={1.25} className="text-[#8E9AA6] shrink-0" />
            <span className="text-xs text-[#8E9AA6] uppercase tracking-[0.2em]">
              {unreadCount > 0
                ? `${unreadCount} unread update${unreadCount === 1 ? "" : "s"} on your reservations.`
                : "Driver status notifications will appear here as your chauffeur is dispatched."}
            </span>
          </div>

          {/* Tabs */}
          <div
            data-testid="dashboard-tabs"
            className="flex gap-2 mb-10 border-b border-white/10"
          >
            {TABS.map((t) => {
              const isActive = tab === t.key;
              const count =
                t.key === "active" ? active.length : t.key === "past" ? past.length : null;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  data-testid={`dashboard-tab-${t.key}`}
                  className={`relative px-6 py-3.5 text-xs uppercase tracking-[0.25em] transition-colors ${
                    isActive ? "text-white" : "text-[#8E9AA6] hover:text-white"
                  }`}
                >
                  {t.label}
                  {count !== null && (
                    <span className="ml-2 text-[10px] text-[#8E9AA6]">{count}</span>
                  )}
                  {isActive && (
                    <span className="absolute -bottom-px left-2 right-2 h-px bg-white" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          {tab === "profile" ? (
            <ProfilePanel user={user} profile={profile} onLogout={() => { logout(); navigate("/"); }} />
          ) : (
            <BookingsList
              loading={bookingsLoading}
              error={bookingsError}
              bookings={tab === "active" ? active : past}
              emptyText={
                tab === "active"
                  ? "You have no upcoming reservations. Reserve your first journey."
                  : "No past bookings yet."
              }
              tab={tab}
              onOpen={setOpenBooking}
            />
          )}
        </div>
      </section>

      {openBooking && (
        <BookingDetailDrawer
          booking={openBooking}
          onClose={() => setOpenBooking(null)}
        />
      )}
    </Layout>
  );
}

function BookingsList({ loading, error, bookings, emptyText, tab, onOpen }) {
  if (loading) {
    return (
      <div className="text-center py-16 text-[#8E9AA6] text-xs uppercase tracking-[0.3em]">
        Loading reservations…
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-12 text-red-400 text-sm">{error}</div>;
  }
  if (bookings.length === 0) {
    return (
      <div
        data-testid={`bookings-empty-${tab}`}
        className="text-center py-20 border border-dashed border-white/10"
      >
        <p className="text-sm text-[#8E9AA6] font-light max-w-md mx-auto">{emptyText}</p>
      </div>
    );
  }
  return (
    <div data-testid={`bookings-list-${tab}`} className="space-y-3">
      {bookings.map((b) => (
        <BookingRow key={b.id} booking={b} onOpen={() => onOpen(b)} />
      ))}
    </div>
  );
}

function BookingRow({ booking, onOpen }) {
  const isHourly = booking.service_type === "hourly";
  return (
    <div
      data-testid={`booking-row-${booking.id}`}
      className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-center backdrop-blur-xl bg-[#121214]/60 border border-white/10 px-6 py-5 transition-all duration-300 hover:border-white/30 hover:bg-[#121214]/80"
    >
      <div className="md:col-span-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-1">
          Pickup
        </div>
        <div className="text-sm text-white font-light">{fmt(booking.pickup_datetime)}</div>
      </div>
      <div className="md:col-span-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-1">
          {isHourly ? "Hourly Charter" : booking.service_type.replace(/_/g, " ")}
        </div>
        <div className="text-sm text-white font-light flex items-start gap-2">
          <MapPin size={13} strokeWidth={1.25} className="text-[#8E9AA6] mt-0.5 shrink-0" />
          {isHourly ? (
            <span>
              {booking.pickup_address}
              <span className="text-[#8E9AA6] text-xs ml-2">
                · {booking.duration_hours}h
              </span>
            </span>
          ) : (
            <span className="truncate">
              {booking.pickup_address}
              <span className="text-[#8E9AA6] mx-2">→</span>
              {booking.dropoff_address}
            </span>
          )}
        </div>
      </div>
      <div className="md:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-1">
          Vehicle
        </div>
        <div className="text-sm text-white font-light">
          {booking.vehicle_category.replace(/_/g, " ")}
        </div>
      </div>
      <div className="md:col-span-1">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-1">
          Total
        </div>
        <div className="font-serif text-lg text-white">${booking.total_price.toFixed(2)}</div>
      </div>
      <div className="md:col-span-1 flex md:justify-center">
        <span
          data-testid={`booking-status-${booking.id}`}
          className="text-[10px] uppercase tracking-[0.25em] text-[#0B0B0C] bg-[#E6E6E6] px-2.5 py-1"
        >
          {STATUS_LABELS[booking.status] || booking.status}
        </span>
      </div>
      <div className="md:col-span-1 flex md:justify-end">
        <button
          onClick={onOpen}
          data-testid={`booking-view-${booking.id}`}
          className="text-[11px] uppercase tracking-[0.25em] text-white hover:text-[#8E9AA6] transition-colors"
        >
          View →
        </button>
      </div>
    </div>
  );
}

function ProfilePanel({ user, profile, onLogout }) {
  const rows = [
    { label: "Email", value: user.email },
    { label: "Phone", value: user.phone || "—" },
    { label: "Role", value: user.role },
    { label: "First Name", value: profile?.first_name || "—" },
    { label: "Last Name", value: profile?.last_name || "—" },
    { label: "Company", value: profile?.company_name || "—" },
    {
      label: "Member Since",
      value: new Date(user.created_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    },
  ];
  return (
    <div data-testid="account-profile-card" className="backdrop-blur-xl bg-[#121214]/60 border border-white/10">
      <div className="px-8 md:px-10 py-6 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8E9AA6]">Profile</div>
          <div className="font-serif text-2xl text-white mt-1">
            {[profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || user.email}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#0B0B0C] bg-[#E6E6E6] px-3 py-1.5">
          {user.role}
        </span>
      </div>
      <dl className="divide-y divide-white/10">
        {rows.map((r) => (
          <div
            key={r.label}
            className="grid grid-cols-1 sm:grid-cols-3 gap-2 px-8 md:px-10 py-5"
          >
            <dt className="text-[11px] uppercase tracking-[0.3em] text-[#8E9AA6] self-center">
              {r.label}
            </dt>
            <dd className="sm:col-span-2 text-sm text-white font-light">{String(r.value)}</dd>
          </div>
        ))}
      </dl>
      <div className="px-8 md:px-10 py-6 border-t border-white/10">
        <button
          onClick={onLogout}
          data-testid="account-logout-btn"
          className="inline-flex items-center justify-center bg-transparent border border-white/30 text-white hover:bg-white hover:text-black px-9 py-3.5 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
